import type { ChipModelDto, ProjectDto } from '@/schemas/chip';
type ChipInstanceDto = ChipModelDto['editingInstanceDto'];
import type { LogicEdgeDto, LogicGraphDto } from '@/schemas/logic';
import { OwnedNodeRecords, type NodeSegmentPacket } from './NodeRecords';
import { legacyGraphRecords, type GraphRecordSource } from './GraphRecordSource';
import {
  SnapshotBlock,
  SnapshotBlockBuilder,
  type SnapshotCapture,
  type SnapshotPacket,
} from './SnapshotBlocks';
import { validateStateValue } from './StateValue';
import { validateContinuationValue } from './ContinuationValue';

export interface GraphSnapshot {
  readonly nodes: OwnedNodeRecords;
  readonly edges: LogicEdgeDto[];
}
export type InstanceSnapshot = Omit<ChipInstanceDto, 'logicGraphDto'> & {
  logicGraphDto: GraphSnapshot;
};
export type ChipSnapshot = Omit<
  ChipModelDto,
  'chipInstanceDtos' | 'editingInstanceDto'
> & {
  chipInstanceDtos: InstanceSnapshot[];
  editingInstanceDto: InstanceSnapshot;
};
export type ProjectSnapshot = Omit<ProjectDto, 'chips'> & { chips: ChipSnapshot[] };
type GraphPacket = { nodes: NodeSegmentPacket[]; edges: LogicEdgeDto[] };
type InstancePacket = Omit<ChipInstanceDto, 'logicGraphDto'> & {
  logicGraphDto: GraphPacket;
};
type ChipPacket = Omit<ChipModelDto, 'chipInstanceDtos' | 'editingInstanceDto'> & {
  chipInstanceDtos: InstancePacket[];
  editingInstanceDto: InstancePacket;
};
export type ProjectSnapshotData = Omit<ProjectDto, 'chips'> & { chips: ChipPacket[] };
export interface ProjectSnapshotPacket {
  project: ProjectSnapshotData;
  blocks: SnapshotPacket;
}
const MAX_NODES = 1_000_000,
  MAX_GRAPHS = 100_000,
  MAX_IDENTITY_UNITS = 256 * 1024 * 1024;
const ownedChips = new WeakSet<ChipSnapshot>();
const comparisonChips = new WeakSet<ChipSnapshot>();
// Public constructors cannot confer the builder's immutable metadata proof.
const workerCaptures = new WeakSet<CapturedProjectSnapshot>();
const workerGraphs = new WeakSet<GraphSnapshot>();
export function isOwnedChipSnapshot(chip: ChipSnapshot): boolean {
  return ownedChips.has(chip);
}

/** Only transport-cloned or decoded metadata has the plain-data proof used by
 * comparison. Public builder inputs can override array methods and retain hooks. */
export function hasOwnedComparisonMetadata(chip: ChipSnapshot): boolean {
  return comparisonChips.has(chip);
}

class ProjectBudget {
  nodes = 0;
  graphs = 0;
  units = 0;
  roots = new Set<SnapshotBlock>();
  add(graph: GraphSnapshot): void {
    this.nodes += graph.nodes.count;
    this.units += graph.nodes.identityCodeUnits;
    if (
      ++this.graphs > MAX_GRAPHS ||
      this.nodes > MAX_NODES ||
      this.units > MAX_IDENTITY_UNITS
    )
      throw new RangeError('Current project expanded data exceeds limit');
    for (const root of graph.nodes.blockRoots()) {
      if (this.roots.has(root))
        throw new TypeError('Node block has multiple graph owners');
      this.roots.add(root);
    }
  }
}

export function validateProjectSnapshot(project: ProjectSnapshot): void {
  const budget = new ProjectBudget();
  const stateBudget = { members: 0, text: 0 };
  for (const chip of project.chips) {
    if (chip.nativeContinuation !== undefined)
      validateContinuationValue(chip.nativeContinuation, stateBudget);
    for (const circuit of [chip.logicCircuitDto, chip.structureCircuitDto])
      for (const tile of circuit.tileManagerDto.tiles)
        validateStateValue(tile.state, stateBudget);
    for (const instance of [...chip.chipInstanceDtos, chip.editingInstanceDto]) {
      budget.add(instance.logicGraphDto);
      instance.logicGraphDto.nodes.validateStateBudget(stateBudget);
    }
  }
}

/** Validate root ownership and counts before reading any file block payloads. */
export function validateProjectSnapshotLayout(
  project: ProjectSnapshotData,
  rootCount: number,
): void {
  const used = new Set<number>();
  let graphs = 0,
    nodes = 0;
  for (const chip of project.chips)
    for (const instance of [...chip.chipInstanceDtos, chip.editingInstanceDto]) {
      const segments = instance.logicGraphDto.nodes;
      if (
        ++graphs > MAX_GRAPHS ||
        !Array.isArray(segments) ||
        segments.length > MAX_NODES
      )
        throw new RangeError('Current project graph limit exceeded');
      for (const segment of segments) {
        if (!segment || typeof segment !== 'object')
          throw new TypeError('Invalid node segment');
        if ('overflow' in segment) {
          if (
            Object.keys(segment).length !== 1 ||
            !Array.isArray(segment.overflow) ||
            segment.overflow.length < 1 ||
            segment.overflow.length > 512
          )
            throw new TypeError('Invalid overflow node segment');
          nodes += segment.overflow.length;
        } else {
          if (
            (Object.hasOwn(segment, 'layout') && segment.layout !== 'native-id-v2') ||
            Object.keys(segment).length !== (segment.layout === 'native-id-v2' ? 3 : 2) ||
            !Number.isSafeInteger(segment.root) ||
            segment.root < 0 ||
            segment.root >= rootCount ||
            !Number.isSafeInteger(segment.count) ||
            segment.count < 1 ||
            segment.count > 512 ||
            used.has(segment.root)
          )
            throw new TypeError('Invalid or shared node segment');
          used.add(segment.root);
          nodes += segment.count;
        }
        if (nodes > MAX_NODES)
          throw new RangeError('Current project node limit exceeded');
      }
    }
  if (used.size !== rootCount) throw new TypeError('Unused current project node roots');
}

/** The mutable DTO input is cloned before capture. Native capture uses the
 * builder directly and never constructs the project-sized node arrays. */
export function snapshotFromDto(input: ProjectDto): CapturedProjectSnapshot {
  const owned = structuredClone(input);
  const builder = new ProjectSnapshotBuilder();
  try {
    const instance = (dto: ChipInstanceDto): InstanceSnapshot => ({
      ...dto,
      logicGraphDto: builder.graph(legacyGraphRecords(dto.logicGraphDto)),
    });
    return builder.finish({
      ...owned,
      chips: owned.chips.map((chip) => ({
        ...chip,
        chipInstanceDtos: chip.chipInstanceDtos.map(instance),
        editingInstanceDto: instance(chip.editingInstanceDto),
      })),
    });
  } catch (error) {
    builder.abandon();
    throw error;
  }
}

function freezeData(value: unknown, seen = new Set<object>()): void {
  if (
    !value ||
    typeof value !== 'object' ||
    value instanceof OwnedNodeRecords ||
    workerGraphs.has(value as GraphSnapshot) ||
    seen.has(value)
  )
    return;
  const prototype = Object.getPrototypeOf(value);
  if (
    Array.isArray(value)
      ? prototype !== Array.prototype
      : prototype !== Object.prototype && prototype !== null
  )
    throw new TypeError('Snapshot metadata must contain plain data');
  seen.add(value);
  for (const child of Object.values(value)) freezeData(child, seen);
  Object.freeze(value);
}

export class ProjectSnapshotBuilder {
  #blocks = new SnapshotBlockBuilder();
  #budget = new ProjectBudget();
  #workerGraphs = new WeakSet<GraphSnapshot>();
  graph(source: GraphRecordSource): GraphSnapshot {
    if (
      source.nodeCount + this.#budget.nodes > MAX_NODES ||
      this.#budget.graphs >= MAX_GRAPHS
    )
      throw new RangeError('Current project capture limit exceeded');
    const graph = {
      nodes: OwnedNodeRecords.captureSource(source, this.#blocks),
      edges: Array.from(source.edges(), (edge) => structuredClone(edge)),
    };
    this.#budget.add(graph);
    // Each edge was cloned above. Freeze before exposing this graph so callers
    // cannot replace or extend its metadata after the cloneability proof.
    freezeData(graph);
    workerGraphs.add(graph);
    this.#workerGraphs.add(graph);
    return graph;
  }
  finish(project: ProjectSnapshot): CapturedProjectSnapshot {
    validateProjectSnapshot(project);
    // Native metadata getters produce owned DTO data; copy only metadata here,
    // never clone or materialize an OwnedNodeRecords cursor.
    const instance = (value: InstanceSnapshot): InstanceSnapshot => {
      const { logicGraphDto, ...metadata } = value;
      return { ...structuredClone(metadata), logicGraphDto };
    };
    const owned: ProjectSnapshot = {
      ...(project.startPaused ? { startPaused: true as const } : {}),
      name: project.name,
      mainChipId: project.mainChipId,
      chips: project.chips.map((chip) => {
        const { chipInstanceDtos, editingInstanceDto, ...metadata } = chip;
        return {
          ...structuredClone(metadata),
          chipInstanceDtos: chipInstanceDtos.map(instance),
          editingInstanceDto: instance(editingInstanceDto),
        };
      }),
    };
    freezeData(owned);
    for (const chip of owned.chips) ownedChips.add(chip);
    const capture = new CapturedProjectSnapshot(owned, this.#blocks.finish());
    if (
      typeof owned.name === 'string' &&
      typeof owned.mainChipId === 'string' &&
      owned.chips.every((chip) =>
        [...chip.chipInstanceDtos, chip.editingInstanceDto].every((value) =>
          this.#workerGraphs.has(value.logicGraphDto),
        ),
      )
    )
      workerCaptures.add(capture);
    return capture;
  }
  abandon(): void {
    this.#blocks.abandon();
  }
}

export function projectBlockRoots(project: ProjectSnapshot): SnapshotBlock[] {
  return project.chips.flatMap((chip) =>
    [...chip.chipInstanceDtos, chip.editingInstanceDto].flatMap((instance) =>
      instance.logicGraphDto.nodes.blockRoots(),
    ),
  );
}

export function projectSnapshotData(
  project: ProjectSnapshot,
  rootIndexes: ReadonlyMap<SnapshotBlock, number>,
): ProjectSnapshotData {
  const instance = (value: InstanceSnapshot): InstancePacket => ({
    ...value,
    logicGraphDto: {
      nodes: value.logicGraphDto.nodes.packet(rootIndexes),
      edges: value.logicGraphDto.edges,
    },
  });
  return {
    ...project,
    chips: project.chips.map((chip) => ({
      ...chip,
      chipInstanceDtos: chip.chipInstanceDtos.map(instance),
      editingInstanceDto: instance(chip.editingInstanceDto),
    })),
  };
}

/** Transport consumes the entire capture. Only scalar identity survives here. */
export class CapturedProjectSnapshot {
  readonly name: string;
  readonly mainChipId: ProjectDto['mainChipId'];
  #project?: ProjectSnapshot;
  #capture: SnapshotCapture;
  constructor(project: ProjectSnapshot, capture: SnapshotCapture) {
    this.#project = project;
    this.#capture = capture;
    this.name = project.name;
    this.mainChipId = project.mainChipId;
  }
  get project(): ProjectSnapshot {
    if (!this.#project) throw new Error('Snapshot already transferred');
    return this.#project;
  }
  consume(): ProjectSnapshotPacket {
    const project = projectSnapshotData(
      this.project,
      new Map(this.#capture.roots.map((root, i) => [root, i])),
    );
    // Clone non-buffer metadata before detaching, so a rejected extension cannot
    // consume otherwise usable ownership. Node records already passed validation.
    const owned = structuredClone(project);
    const blocks = this.#capture.consumeForTransfer();
    this.#project = undefined;
    return { project: owned, blocks };
  }
  /** Worker-only handoff: fresh packet containers retain immutable metadata until
   * synchronous postMessage takes its own clone. General consumers still get
   * mutable, detached metadata through consume(). */
  consumeForWorker(): ProjectSnapshotPacket {
    if (!workerCaptures.has(this)) return this.consume();
    if (!this.#project) throw new Error('Snapshot already transferred');
    // The certificate applies to private builder data, not a replaceable accessor.
    const project = projectSnapshotData(
      this.#project,
      new Map(this.#capture.roots.map((root, i) => [root, i])),
    );
    // packet() already clones/validates overflow records before buffer detachment.
    const blocks = this.#capture.consumeForTransfer();
    this.#project = undefined;
    return { project, blocks };
  }
}

/** Caller owns a transport clone or decoded file data, never a live DTO. */
export function receiveProjectSnapshotData(
  data: ProjectSnapshotData,
  roots: readonly SnapshotBlock[],
): ProjectSnapshot {
  validateProjectSnapshotLayout(data, roots.length);
  const instance = (value: InstancePacket): InstanceSnapshot => ({
    ...value,
    logicGraphDto: {
      nodes: OwnedNodeRecords.receive(value.logicGraphDto.nodes, roots),
      edges: value.logicGraphDto.edges,
    },
  });
  const project: ProjectSnapshot = {
    ...data,
    chips: data.chips.map((chip) => ({
      ...chip,
      chipInstanceDtos: chip.chipInstanceDtos.map(instance),
      editingInstanceDto: instance(chip.editingInstanceDto),
    })),
  };
  freezeData(project);
  validateProjectSnapshot(project);
  for (const chip of project.chips) {
    ownedChips.add(chip);
    comparisonChips.add(chip);
  }
  return project;
}

export function receiveProjectSnapshot(
  packet: ProjectSnapshotPacket,
  ownedInput = false,
): ProjectSnapshot {
  const project = ownedInput ? packet.project : structuredClone(packet.project);
  validateProjectSnapshotLayout(project, packet.blocks.roots.length);
  return receiveProjectSnapshotData(project, SnapshotBlock.receive(packet.blocks));
}

/** Hydration boundary only. Save, compare and merge must never call this. */
export function projectSnapshotDto(project: ProjectSnapshot): ProjectDto {
  validateProjectSnapshot(project);
  const instance = (value: InstanceSnapshot): ChipInstanceDto => ({
    ...structuredClone({ ...value, logicGraphDto: undefined }),
    logicGraphDto: {
      nodes: Array.from(value.logicGraphDto.nodes.nodes()),
      edges: structuredClone(value.logicGraphDto.edges),
    } as LogicGraphDto,
  });
  return {
    ...project,
    chips: project.chips.map((chip) => ({
      ...structuredClone({
        ...chip,
        chipInstanceDtos: undefined,
        editingInstanceDto: undefined,
      }),
      chipInstanceDtos: chip.chipInstanceDtos.map(instance),
      editingInstanceDto: instance(chip.editingInstanceDto),
    })),
  };
}
